import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { CbhIcon, DotSeparatedList, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
// TODO: Use our own Card, Pill instead of Chip
// eslint-disable-next-line no-restricted-imports
import { Box, Card, CardActionArea, CardContent, Chip, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, BaseLink } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { jobTypeOptions, shiftTypeOptions } from "@src/appV2/redesign/PlacementCandidate/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";

import { type HiringFeedWithDetails } from "../../HiringFeed/api/usePaginatedHiringFeed";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { type PayRateMeta } from "../lib/useComputedPay";
import { type PayTransparencyFlag } from "../lib/usePayTransparencyFlag";
import { getPlacementDetailsPath } from "../paths";
import { PlacementPerk } from "../types/fetchPlacements.schema";
import { type PayRate } from "../types/payRate";
import { PlacementCardEventHeader } from "./PlacementCardEventHeader";
import { PlacementCardPay } from "./PlacementCardPay";

export interface PlacementCardDetails {
  id: string;
  status: PlacementWithDetails["status"];
  title: PlacementWithDetails["title"];
  workerTypes: HiringFeedWithDetails["workerTypes"];
  jobShifts: HiringFeedWithDetails["jobShifts"];
  payRange: HiringFeedWithDetails["payRange"];
  perks: HiringFeedWithDetails["perks"];
  hiringEvents: HiringFeedWithDetails["hiringEvents"];
  distance: HiringFeedWithDetails["distance"];
  isLicenseEligible: HiringFeedWithDetails["meta"]["isLicenseEligible"];
  interviewSettings: HiringFeedWithDetails["interviewSettings"];
  applications: PlacementWithDetails["applications"];
  workplace: {
    id: string;
    name: string;
  };
}

interface PlacementCardProps {
  placement: PlacementCardDetails;
  placementCandidateId: string;
  pageIndex: number;
  placementIndex: number;
  payRate: PayRate | undefined;
  payRateMeta: PayRateMeta | undefined;
  loadingPayData: boolean;
  payRateExperiment: PayTransparencyFlag;
}

const PAY_RATE_LABELS = {
  RANGE: "Pay range",
  FROM: "Starting from",
  UP_TO: "Up to",
  TBD: "Pay rate TBD",
} as const;

export function PlacementCard(props: PlacementCardProps) {
  const {
    placement,
    placementCandidateId,
    pageIndex,
    placementIndex,
    payRate,
    payRateMeta,
    loadingPayData,
    payRateExperiment,
  } = props;

  const worker = useDefinedWorker();

  const hasPayRateRange =
    isDefined(payRate) &&
    isDefined(payRate.min) &&
    isDefined(payRate.max) &&
    payRate.max !== payRate.min;

  function getPayRateLabel() {
    if (payRateExperiment.enabled && payRate?.externallyDerived) {
      return payRateExperiment.labelText;
    }

    if (hasPayRateRange) {
      return PAY_RATE_LABELS.RANGE;
    }

    if (!hasPayRateRange && placement.payRange?.minPay && !placement.payRange?.maxPay) {
      return PAY_RATE_LABELS.FROM;
    }

    if (!hasPayRateRange && placement.payRange?.maxPay) {
      return PAY_RATE_LABELS.UP_TO;
    }

    return PAY_RATE_LABELS.TBD;
  }

  const payRateLabel = getPayRateLabel();

  const combinedShiftTypes = [
    ...new Set(placement.jobShifts.flatMap((jobShift) => jobShift.shiftTypes) ?? []),
  ];

  const shiftTypeAndSpecialPerkElements = [
    ...combinedShiftTypes.map((shiftType) => {
      const shiftTypeOption = shiftTypeOptions.find((option) => option.value === shiftType);
      if (!isDefined(shiftTypeOption)) {
        return null;
      }

      return (
        <Box key={shiftType} component="span">
          <CbhIcon type={shiftTypeOption.icon} size="xSmall" />
        </Box>
      );
    }),
    placement.perks?.includes(PlacementPerk.TWELVE_SHIFTS_AVAILABLE) ? (
      <Text key="12h-shifts" variant="body2" color="text.secondary">
        12h shifts
      </Text>
    ) : null,
  ].filter(Boolean);

  const startDates = placement.hiringEvents
    ?.map((event) => {
      return event.fixedSchedule
        ? formatShortDateWithWeekday(parseISO(event.fixedSchedule.startDate), {
            timeZone: worker.tmz,
          })
        : undefined;
    })
    .filter(isDefined);

  let cardType: "open-house" | "interview-days" | undefined;

  if (isDefined(placement.hiringEvents)) {
    cardType = "open-house";
  } else if (isDefined(placement.interviewSettings)) {
    cardType = "interview-days";
  }

  return (
    <Card
      variant="elevation"
      sx={{
        backgroundColor: (theme) => theme.palette.background.tertiary,
        border: (theme) =>
          isDefined(placement.hiringEvents)
            ? `1px solid ${theme.palette.placement?.openHouse.border}`
            : "none",
        background: (theme) =>
          isDefined(placement.hiringEvents)
            ? theme.palette.placement?.openHouse.gradient
            : theme.palette.background.tertiary,
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={getPlacementDetailsPath(placement.id, placementCandidateId, cardType)}
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
            placementCandidateId,
            placementDetails: placement,
            page: pageIndex + 1,
            listIndex: placementIndex + 1,
            source: "placement-card",
            cardType,
            hiringEvents: placement.hiringEvents?.map((event) => {
              return {
                id: event.id,
                name: event.type,
                startDate: event.fixedSchedule?.startDate,
                endDate: event.fixedSchedule?.endDate,
              };
            }),
          });
        }}
      >
        <CardContent>
          <Stack spacing={4} padding={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Stack spacing={5} flex={1}>
                {!isDefined(placement.applications) && (
                  <PlacementCardEventHeader placement={placement} startDates={startDates} />
                )}

                <Stack direction="column" spacing={4}>
                  <Text semibold variant="h6" color={(theme) => theme.palette.text.primary}>
                    {placement.title}
                  </Text>
                  <Stack direction="row" spacing={2} alignItems="baseline">
                    <Text variant="body1">{placement.workplace.name}</Text>
                    {isDefined(placement.distance) && (
                      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                        {formatDistanceInMiles(placement.distance)}
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
              <DotSeparatedList>{shiftTypeAndSpecialPerkElements}</DotSeparatedList>
              <Text variant="body2">
                {[...new Set(placement.jobShifts.flatMap((jobShift) => jobShift.jobType))]
                  .map(
                    (jobType) => jobTypeOptions.find((option) => option.value === jobType)?.label
                  )
                  .join(", ")}
              </Text>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
              <Stack alignItems="center" spacing={1}>
                {isDefined(placement.workerTypes) && (
                  <Chip
                    label={
                      placement.workerTypes.length > 1
                        ? `${placement.workerTypes[0]} + ${placement.workerTypes.length - 1} more`
                        : placement.workerTypes[0]
                    }
                    variant="filled"
                    size="small"
                    sx={{
                      border: "0.5px solid",
                      borderColor: (theme) => theme.palette.border?.primary,
                      backgroundColor: (theme) => theme.palette.background.tertiary,
                    }}
                  />
                )}
              </Stack>
              <PlacementCardPay
                placement={{
                  id: placement.id,
                  workplaceId: placement.workplace.id,
                  workerTypes: placement.workerTypes,
                  jobShifts: placement.jobShifts,
                  attributes: {
                    payRange: placement.payRange,
                  },
                }}
                placementCandidateId={placementCandidateId}
                payRate={payRate}
                payRateMeta={payRateMeta}
                label={payRateLabel ?? undefined}
                loadingPayData={loadingPayData}
                payRateExperiment={payRateExperiment}
              />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
